import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { Store } from '@ngrx/store';
import { Map, Set } from 'immutable';
import { map, Observable, takeUntil, withLatestFrom } from 'rxjs';

import { DcCoreElementType } from '../../../../core/dc-core-types';
import { AppSelector } from '../../../../core/store';
import {
	AppPermissionsConfigComponent,
	Configuration,
} from '../../../../ui/components/app-permissions-configurator/app-permissions-config.component';
import { PermissionConfigItemEntity } from '../../../../ui/components/app-permissions-configurator/permission-config-item.entity';
import {
	PermissionConfig,
	PermissionConfigListEntity,
} from '../../../../ui/components/app-permissions-configurator/permission-config-list.entity';
import { refreshMembersAccess, SubscriptionSelector } from '../../../store';
import { MemberAccessEntity } from './member-access.entity';
import { PermissionListProvider } from './permission-list.provider';

@Component({
	selector: 'app-permissions-config-modal',
	templateUrl: './permissions-config-modal.component.html',
	styleUrls: ['./permissions-config-modal.component.scss'],
	providers: [PermissionListProvider],
})
export class PermissionsConfigModalComponent<T>
	extends DcBaseComponent
	implements OnInit
{
	@ViewChild(AppPermissionsConfigComponent)
	private readonly permissionConfigCmp: AppPermissionsConfigComponent | null = null;

	public vo$: Observable<{
		list: PermissionConfigListEntity;
	}>;

	public config: Configuration = {
		usePanel: true,
		columnWidth: 250,
		inViewMode: false,
	};

	public constructor(
		private readonly store: Store,
		private readonly selector: AppSelector,
		private readonly modalService: AppModalService,
		private readonly subscriptionSelector: SubscriptionSelector,
		private readonly permissionProvider: PermissionListProvider,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();

		const list$ = this.subscriptionSelector
			.getMembersAccessSubscription$()
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.selector.getAvailablePermissions$()),
				map(([membersAccess, permissions]) => {
					const elements = this.generateElements(membersAccess);
					const subList = permissions.get(DcCoreElementType.DATALINK);
					const columns = subList ? this.generateColumns(subList) : [];
					return this.permissionProvider.setList(columns, elements);
				})
			);

		this.vo$ = combineAll({
			list: list$,
		});
	}

	public override ngOnInit(): void {
		super.ngOnInit();
	}

	public async updatePermissions(): Promise<void> {
		if (!this.permissionConfigCmp) {
			return;
		}

		const elements = await this.permissionConfigCmp.getUpdatedPermissions();

		const updated = [...elements.values()].map((item) =>
			MemberAccessEntity.build({
				id: item.id,
				memberUuid: item.element,
				isGroup: item.isGroup,
				permissions: item.permissions,
			})
		);

		this.modalService.closeActiveModal({
			membersAccess: updated,
		});
	}

	public refresh(): void {
		this.store.dispatch(refreshMembersAccess());
	}

	private generateColumns(cols: Set<string>): Array<PermissionConfig> {
		return cols
			.toList()
			.toArray()
			.reduce<Array<PermissionConfig>>((acc, action) => {
				if (!action) {
					return acc;
				}

				acc.push({
					permission: action,
					isDisabled: false,
					toggled: false,
				});

				return acc;
			}, Array<PermissionConfig>());
	}

	private generateElements(
		els: Map<string, MemberAccessEntity>
	): Array<PermissionConfigItemEntity> {
		return els
			.toList()
			.toArray()
			.reduce<Array<PermissionConfigItemEntity>>((acc, access) => {
				let mappedPermissions = Set<string>();

				access.permissions.forEach((perm) => {
					mappedPermissions = mappedPermissions.add(perm);
				});

				acc.push(
					PermissionConfigItemEntity.build({
						id: access.id,
						element: access.memberUuid,
						isGroup: access.isGroup,
						permissions: mappedPermissions,
						allToggled: false,
					})
				);

				return acc;
			}, Array<PermissionConfigItemEntity>());
	}
}
